import { Subscription } from 'rxjs';
import { MediaPlayerFileSystem } from 'src/mediaPlayerFileSystem';
import { ScheduledTickerResult, Resource, TickerItem } from './tickerScheduler';
import { HtmlUtils } from 'src/htmlUtils';

import { fileManager } from 'src/fileManager';
import { FileUtils } from 'src/fileUtils';
import { TickerFontManager } from './tickerFontManager';

namespace TickerStyleGeneration {

    function scaleFontSize(fontSize: string, multiplier: number) : string {
        const fontSizeNumeric = parseInt(fontSize, 10);
        if (fontSizeNumeric) {
            return `${fontSizeNumeric * multiplier}`;
        } else {
            return fontSize;
        }
    }

    export function generateCssNameValuePairs(
        resource: Resource | undefined,
        backgroundColor: string,
        tickerFontManager: TickerFontManager,
        sizeMultipliers: {width: number; height: number}
    ) {
        const nameAndValues: { [key:string]:string } = {};
        if (resource && resource.textStyle) {
            if (resource.textStyle.fontWeight) {
                nameAndValues['font-weight'] = resource.textStyle.fontWeight;
            }
            if (resource.textStyle.color) {
                nameAndValues['color'] = resource.textStyle.color;
            }
            if (resource.textStyle.fontStyle) {
                nameAndValues['font-style'] = resource.textStyle.fontStyle;
            }
            if (resource.textStyle.fontSize) {
                nameAndValues['font-size'] = `${scaleFontSize(resource.textStyle.fontSize, sizeMultipliers.height)}pt`;
            }
        }
        if (resource && resource.fontFamily && resource.fontFamily.name) {
            nameAndValues['font-family'] = tickerFontManager.ensureFontFamilyIsInStyleSheet(resource.fontFamily.fileName);
        }
        if (backgroundColor) {
            nameAndValues['background-color'] = backgroundColor;
        }
        return nameAndValues;
    }

    export function createStylesheetElement(
        selector : string,
        nameAndValues? : {[key:string] : string}
    ) : string {
        if (!nameAndValues) {
            return "";
        }
        let resourceCss: string = `${selector} {`;
        for(const name in nameAndValues) {
            resourceCss = `${resourceCss} ${name}:${nameAndValues[name]};`;
        }
        resourceCss = `${resourceCss} }`;
        return resourceCss;
    }
}

namespace CssClassNames {
    export const parentTickerElement = 'divTicker';
    export const mainPanel = 'divTickerMainPanel';
    export const leftPanel = 'divTickerLeftPanel';
    export const rightPanel = 'divTickerRightPanel';
    export const divMarquee = 'divMarquee';
    export const marqueeChild = 'marqueeChild';
    export const panelContentHolder = 'panelContentHolder';
}


export class TickerRenderer {
    private readonly tickerId = `divTicker_${HtmlUtils.generateUniqueId()}`;
    private readonly styleElementId = `style_${this.tickerId}`;

    private tickerImageWatchers: Subscription[] = [];

    private readonly tickerFontManager: TickerFontManager;

    constructor(
        private mediaPlayerFileSystem: MediaPlayerFileSystem,
        private sourceFolder: string,
        private element: HTMLElement,
        private sizeMultipliers: { width: number, height: number },
        private scheduledTickerResult: ScheduledTickerResult
    ) {
        this.tickerFontManager = new TickerFontManager(this.styleElementId, sourceFolder, mediaPlayerFileSystem);
    }

    buildTickerHtml() {
        this.generateTickerStyleAndAddToDocument();

        const tickerElement = document.createElement("div");
        tickerElement.className = CssClassNames.parentTickerElement;
        tickerElement.id = this.tickerId;

        const leftTickerPanel = document.createElement("div");
        leftTickerPanel.className = CssClassNames.leftPanel;
        const leftTickerPanelContentHolder = document.createElement("div");
        leftTickerPanelContentHolder.className = CssClassNames.panelContentHolder;
        leftTickerPanel.appendChild(leftTickerPanelContentHolder);
        tickerElement.appendChild(leftTickerPanel);


        const rightTickerPanel = document.createElement("div");
        rightTickerPanel.className = CssClassNames.rightPanel;
        const rightPanelContentHolder = document.createElement("div");
        rightPanelContentHolder.className = CssClassNames.panelContentHolder;
        rightTickerPanel.appendChild(rightPanelContentHolder);
        tickerElement.appendChild(rightTickerPanel);

        const mainTickerPanel = document.createElement("div");
        mainTickerPanel.className = CssClassNames.mainPanel;
        tickerElement.appendChild(mainTickerPanel);

        const mainPanelMarquee = document.createElement("div");
        mainPanelMarquee.className = CssClassNames.divMarquee;
        mainTickerPanel.appendChild(mainPanelMarquee);

        const marqueeChildren = document.createElement("div");
        marqueeChildren.className = CssClassNames.marqueeChild;
        mainPanelMarquee.appendChild(marqueeChildren);

        this.element.textContent = '';
        this.element.appendChild(tickerElement);

        const height = this.element.offsetHeight * 0.8;
        const parentWidth = this.element.offsetWidth;

        const leftPanelItems = this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay.leftPanel.items;

        if (leftPanelItems.length !== 0){
            this.generateTickerPanelItems(leftTickerPanelContentHolder, leftPanelItems, height);
        }

        const mainPanelItems = this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay.mainPanel.items;
        let speed = 10;
        if (mainPanelItems.length !== 0) {

            if (this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay.tickerSpeed) {
                try {
                    speed = parseInt(this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay.tickerSpeed);
                } catch (error) {

                }
            }
            this.generateTickerPanelItems(marqueeChildren, mainPanelItems, height);
        }

        //use when the width of elements in main panel is less than width of screen
        const mainPanelWidth = marqueeChildren.offsetWidth;

        if (mainPanelWidth !== 0 && mainPanelWidth < parentWidth) {
            const count = parentWidth / mainPanelWidth + 1;
            const marqueeHtml = marqueeChildren.innerHTML;

            for (var i = 0; i < count; i++) {
                mainPanelMarquee.insertAdjacentHTML("beforeend", marqueeHtml);
            }
        }
        mainPanelMarquee.insertAdjacentHTML("beforeend", mainPanelMarquee.innerHTML);

        let moveSpeed = this.calculateSpeed(speed, marqueeChildren.offsetWidth, this.element.offsetWidth);
        //end

        this.generateTickerAnimationCss(marqueeChildren.offsetWidth, moveSpeed, mainPanelMarquee);


        //use to detect the size changed event of ticker cause by font or image loaded
        HtmlUtils.divSizeChanged(marqueeChildren, () => {
            mainPanelMarquee.style.animation = null;
            moveSpeed = this.calculateSpeed(speed, marqueeChildren.offsetWidth, this.element.offsetWidth);
            this.generateTickerAnimationCss(marqueeChildren.offsetWidth, moveSpeed, mainPanelMarquee);
        });

        const rightPanelItems = this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay.rightPanel.items;

        if (rightPanelItems.length !== 0) {
            this.generateTickerPanelItems(rightPanelContentHolder, rightPanelItems, height);
        }
    }

    dispose() {
        this.clearTickerImageWatchers();
        this.tickerFontManager.dispose();
        let style = document.getElementById(this.styleElementId);
        if (style) {
            style.remove();
        }
        const styleId = 'tickerAnimationstyle' + this.tickerId;
        style = document.getElementById(styleId);
        if (style) {
            style.remove();
        }
    }

    calculateSpeed(speed: number, tickerMainPanelWidth: number, screenWidth: number) {
        return tickerMainPanelWidth / ((screenWidth * speed) / 60);
    }

    generateTickerAnimationCss(width: number, speed: number, marquee: HTMLElement) {
        const styleId = 'tickerAnimationstyle' + this.tickerId;
        let style = document.getElementById(styleId);
        if (style) {
            style.remove();
        }

        style = document.createElement("style");
        style.id = styleId;
        style.append('@-webkit-keyframes marquee' + this.tickerId + '{0% { -webkit-transform: translateX(0px);}100% { -webkit-transform: translateX(-' + width + 'px);}');

        document.head.appendChild(style);
        marquee.style.animation = 'marquee' + this.tickerId + ' ' + speed + 's linear infinite';
    }

    private generateTickerStyleAndAddToDocument() {
        const parentStyleRef = this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay.textStyleRef;
        const existingStyle = document.getElementById(this.styleElementId);
        if (existingStyle) {
            existingStyle.remove();
        }

        const style = document.createElement("style");
        style.id = this.styleElementId;

        const currentTickerDisplay = this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay;
        const mainTextStyleRef = currentTickerDisplay.textStyleRef;
        const mainTextStyleResource = this.scheduledTickerResult.resources.find(r => r.key ==  mainTextStyleRef);
        const npv = TickerStyleGeneration.generateCssNameValuePairs(
            mainTextStyleResource,
            currentTickerDisplay.tickerDisplayBackground,
            this.tickerFontManager,
            this.sizeMultipliers
        );
        const styleSheetElement = TickerStyleGeneration.createStylesheetElement(
            `#${this.tickerId}`,
            npv
        );
        if (styleSheetElement) {
            style.append(styleSheetElement);
        }


        // Assign the styles to the panel
        const panelAndClasses = [
            { panel: currentTickerDisplay.leftPanel, className: CssClassNames.leftPanel },
            { panel: currentTickerDisplay.mainPanel, className: CssClassNames.mainPanel },
            { panel: currentTickerDisplay.rightPanel, className: CssClassNames.rightPanel }
        ];
        for(let pAndC of panelAndClasses) {
            const panel = pAndC.panel;
            if ((panel)  && (panel.backgroundColor || panel.textStyleRef)) {
                const panelResource: (Resource | undefined) = panel.textStyleRef ? this.scheduledTickerResult.resources.find(r => r.key ==  panel.textStyleRef) : undefined;
                const npv = TickerStyleGeneration.generateCssNameValuePairs(
                    panelResource,
                    panel.backgroundColor || currentTickerDisplay.tickerDisplayBackground,
                    this.tickerFontManager,
                    this.sizeMultipliers
                );
                const styleSheetElement = TickerStyleGeneration.createStylesheetElement(
                    `#${this.tickerId} .${pAndC.className}`,
                    npv
                );
                if (styleSheetElement) {
                    style.append(styleSheetElement);
                }
            }
        }
        this.scheduledTickerResult.resources.forEach((resource) => {

            style.append(
                TickerRenderer.generateResourceCss(
                    '#' + this.tickerId + ' span[name="' + resource.key + '"]', 
                    resource, 
                    null, 
                    this.tickerFontManager,
                    this.sizeMultipliers
                )
            );

            if (parentStyleRef && parentStyleRef !== '') {
                if (parentStyleRef === resource.key) {
                    const backgroundColor = this.scheduledTickerResult.tickerDisplayAssignment.tickerDisplay.tickerDisplayBackground;
                    style.append(
                        TickerRenderer.generateResourceCss(
                            '#' + this.tickerId,
                            resource,
                            backgroundColor, 
                            this.tickerFontManager,
                            this.sizeMultipliers
                        )
                    );
                }
            }
        });

        document.head.appendChild(style);
    }

    watchTickerImages(imageItems: string[]) {
        const imageNames: string[] = [];

        imageItems.forEach((name) => {
            if (imageNames.indexOf(name) == -1) {
                imageNames.push(name);
            }
        });

        imageNames.forEach((name) => {
            const visualFeedContentFolderName = fileManager.getMediaPlayerFolderName(fileManager.mediaPlayerFolder.VisualFeedContent);
            const imageFilePath = `/${visualFeedContentFolderName}/${this.sourceFolder}/${name}`;
            const watch = this.mediaPlayerFileSystem.watchMediaFileArrived(imageFilePath)
                .subscribe({
                    next: async (fileEntry: FileEntry) => {
                        const fileUrl = await FileUtils.getFileUrl(name, this.sourceFolder);
                        const elements: Element[] = Array.prototype.slice.call(document.querySelectorAll('#' + this.tickerId + ' img[name="' + name + '"]'));
                        elements.forEach((element, index) => {
                           element.setAttribute('src', fileUrl + "?version=" + new Date().getTime());
                        });
                    }
                });

            this.tickerImageWatchers.push(watch);
        });
    }

    clearTickerImageWatchers() {
        this.tickerImageWatchers.forEach((watcher) => {
            watcher.unsubscribe();
        });

        this.tickerImageWatchers = [];
    }

    static generateResourceCss(
        selector: string,
        resource: Resource,
        backgroundColor: string,
        tickerFontManager: TickerFontManager,
        sizeMultipliers: {width: number; height: number}
    ) : string {
        const nameAndValues = TickerStyleGeneration.generateCssNameValuePairs(
            resource,
            backgroundColor,
            tickerFontManager,
            sizeMultipliers
        );
        return TickerStyleGeneration.createStylesheetElement(selector, nameAndValues);
    }

    generateTickerPanelItems(targetElement: HTMLElement, items: TickerItem[], height: number) {
        const images: string[] = [];

        let itemElement: HTMLElement;
        items.forEach((item) => {
            if (item.Type === 'Text') {
                itemElement = document.createElement("span");
                itemElement.className = "tickerText";
                itemElement.innerText = item.Text;
                if (item.TextStyleRef) {
                    itemElement.setAttribute("name", item.TextStyleRef);
                }
            } else {
                itemElement = document.createElement("img");
                itemElement.style.height = height + "px";
                itemElement.setAttribute("name", item.FileName);
                images.push(item.FileName);
            }
            targetElement.appendChild(itemElement);
        });

        if (images.length !== 0) {
            this.watchTickerImages(images);
        }
    }
}
